// SAB-432: Pure derivation helpers over the eligibility data.
//
// The functions here accept a minimal projection so both call sites — the
// TS-config-sourced marketing footer and the DB-sourced TrustLegality
// section — can pass adapter-mapped arrays and produce identical output.
//
// No I/O, no fetching. Tests in derive.test.ts pin the modal/exception
// formatting so a counsel-driven age-matrix change shows up in CI before
// it ships.

export interface AgeLineInput {
  code: string;
  minAge: number;
  fantasyLegal: boolean;
}

export type AgeLineFormat = 'full' | 'summary';

/**
 * Derived sentence about minimum-age requirements across legal states.
 *
 *   'full'    — "{baseAge}+ in most states; {age}+ in {codes}; ...". Used on
 *               the dedicated /state-availability page and TrustLegality.
 *   'summary' — terse form for the marketing footer:
 *                 "18+"                         (no exceptions)
 *                 "18+, higher in some states"  (only higher exceptions)
 *                 "18+, lower in some states"   (only lower exceptions)
 *                 "18+, varies by state"        (mixed higher and lower)
 *
 * Both formats derive from the SAME bucketing pass, so the footer
 * cannot drift from the detailed view. Restricted states are ignored
 * for the modal-age computation either way. Returns '' when no legal
 * states are present.
 */
export function deriveAgeLine(
  states: AgeLineInput[],
  format: AgeLineFormat = 'full',
): string {
  const legal = states.filter((s) => s.fantasyLegal);
  if (legal.length === 0) return '';

  const byAge = new Map<number, string[]>();
  for (const s of legal) {
    const arr = byAge.get(s.minAge) ?? [];
    arr.push(s.code);
    byAge.set(s.minAge, arr);
  }

  // Modal age = largest bucket. Ties resolve to the lower age (deterministic).
  let baseAge = 18;
  let baseCount = 0;
  for (const [age, codes] of byAge) {
    if (codes.length > baseCount || (codes.length === baseCount && age < baseAge)) {
      baseAge = age;
      baseCount = codes.length;
    }
  }

  const exceptionAges = Array.from(byAge.keys()).filter((a) => a !== baseAge);

  if (format === 'summary') {
    if (exceptionAges.length === 0) return `${baseAge}+`;
    const hasHigher = exceptionAges.some((a) => a > baseAge);
    const hasLower = exceptionAges.some((a) => a < baseAge);
    if (hasHigher && !hasLower) return `${baseAge}+, higher in some states`;
    if (hasLower && !hasHigher) return `${baseAge}+, lower in some states`;
    return `${baseAge}+, varies by state`;
  }

  // 'full'
  const exceptions: string[] = [];
  for (const age of exceptionAges.sort((a, b) => a - b)) {
    const codes = (byAge.get(age) ?? []).slice().sort();
    exceptions.push(`${age}+ in ${codes.join(', ')}`);
  }
  return exceptions.length === 0
    ? `${baseAge}+ in all eligible states.`
    : `${baseAge}+ in most states; ${exceptions.join('; ')}.`;
}
